import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { BellRingingLineIcon, CalendarLineIcon, type MenuProps, TelehealthLineIcon, message } from 'ui';
import { CreateEventModal, CreateReminderModal, TelehealthRequestStepsModal } from 'features/events';
import type { SlotStart } from '../../types';

type P = {
  onActionSelected?: () => void;
  showRequestTelehealthVisit?: boolean;
  slotStart?: SlotStart;
};

export const useCalendarActionsMenu = ({ onActionSelected, showRequestTelehealthVisit = false, slotStart }: P = {}) => {
  const [isCreateModalOpened, setIsCreateModalOpened] = useState(false);
  const [isTelehealthRequestModalOpened, setIsTelehealthRequestModalOpened] = useState(false);
  const [isCreateReminderModalOpened, setIsCreateReminderModalOpened] = useState(false);
  const { canRequestTelehealthEvent } = usePermissions();
  const { t } = useTranslation();

  const items = useMemo<MenuProps['items']>(
    () => [
      {
        icon: <CalendarLineIcon />,
        key: 'create-event',
        label: t('Create Event'),
        onClick() {
          setIsCreateModalOpened(true);
          onActionSelected?.();
        },
      },
      ...(showRequestTelehealthVisit && canRequestTelehealthEvent({})
        ? [
            {
              icon: <TelehealthLineIcon />,
              key: 'request-telehealth-visit',
              label: t('Request telehealth visit'),
              onClick() {
                setIsTelehealthRequestModalOpened(true);
                onActionSelected?.();
              },
            },
          ]
        : []),
      {
        icon: <BellRingingLineIcon />,
        key: 'create-reminder',
        label: t('Set Reminder'),
        onClick() {
          setIsCreateReminderModalOpened(true);
          onActionSelected?.();
        },
      },
    ],
    [canRequestTelehealthEvent, onActionSelected, showRequestTelehealthVisit, t],
  );

  const modals = useMemo(
    () => (
      <>
        {showRequestTelehealthVisit ? (
          <TelehealthRequestStepsModal
            isOpened={isTelehealthRequestModalOpened}
            onClose={() => {
              setIsTelehealthRequestModalOpened(false);
            }}
          />
        ) : null}
        <CreateEventModal
          isOpened={isCreateModalOpened}
          onCancel={() => {
            setIsCreateModalOpened(false);
          }}
          onSuccess={() => {
            message.success('Event created successfully.');
            setIsCreateModalOpened(false);
          }}
          slotStart={slotStart}
        />
        <CreateReminderModal
          isOpened={isCreateReminderModalOpened}
          onCancel={() => {
            setIsCreateReminderModalOpened(false);
          }}
          onSuccess={() => {
            message.success('Reminder created successfully.');
            setIsCreateReminderModalOpened(false);
          }}
          slotStart={slotStart}
        />
      </>
    ),
    [
      isCreateModalOpened,
      isTelehealthRequestModalOpened,
      isCreateReminderModalOpened,
      showRequestTelehealthVisit,
      slotStart,
    ],
  );

  return useMemo(() => ({ items, modals }), [items, modals]);
};
